import React from 'react';
import { getWorkerPool, WorkerTask } from '../workers/workerPool';
import { db } from './database';
import { modelService } from './modelService';
import { tesseractService } from './tesseractService';

export interface ProcessingOptions {
  useGemma?: boolean;
  useTesseract?: boolean;
  preprocessOptions?: {
    grayscale?: boolean;
    threshold?: boolean;
    denoise?: boolean;
    enhance?: boolean;
  };
  tesseractOptions?: {
    psm?: number; // Page segmentation mode
    whitelist?: string; // Character whitelist
  };
  priority?: number;
  language?: string;
}

export interface ExtractionResult {
  id: string;
  documentId: string;
  timestamp: Date;
  processingTime: number;
  gemmaResult?: {
    text: string;
    entities: Array<{ type: string; value: string; confidence: number }>;
    tables: any[];
    metadata: any;
  };
  tesseractResult?: {
    text: string;
    confidence: number;
    blocks: any[];
    lines: any[];
    words?: any[];
    metadata?: {
      language: string;
      processingTime: number;
      engine: string;
      version?: string;
    };
  };
  combinedText?: string;
  status: 'success' | 'partial' | 'failed';
  errors?: string[];
}

class AIProcessingService {
  private workerPool = getWorkerPool();
  private processingQueue: Map<string, ProcessingOptions> = new Map();
  private warmedUp: boolean = false;
  
  async processDocument(
    documentId: string,
    imageData: ImageData | File | Blob,
    options: ProcessingOptions = {}
  ): Promise<ExtractionResult> {
    const startTime = performance.now();
    const extractionId = crypto.randomUUID();
    const errors: string[] = [];
    
    // Store in processing queue
    this.processingQueue.set(extractionId, options);
    
    try {
      // Convert File/Blob to ImageData if needed
      let processedImage: ImageData;
      if (imageData instanceof File || imageData instanceof Blob) {
        processedImage = await this.fileToImageData(imageData);
      } else {
        processedImage = imageData;
      }
      
      // Preprocess if requested
      if (options.preprocessOptions) {
        const preprocessResult = await this.preprocessImage(processedImage, options.preprocessOptions);
        if (preprocessResult) {
          processedImage = preprocessResult;
        }
      }
      
      // Run AI models in parallel
      const promises: Promise<any>[] = [];
      let gemmaResult: ExtractionResult['gemmaResult'];
      let tesseractResult: ExtractionResult['tesseractResult'];
      
      if (options.useGemma !== false) {
        promises.push(
          this.runGemmaExtraction(processedImage, options.priority)
            .then(result => { gemmaResult = result; })
            .catch(error => { errors.push(`Gemma error: ${error.message}`); })
        );
      }
      
      if (options.useTesseract !== false) {
        promises.push(
          this.runTesseractExtraction(processedImage, options)
            .then(result => { tesseractResult = result; })
            .catch(error => { errors.push(`Tesseract error: ${error.message}`); })
        );
      }
      
      // Wait for all processing to complete
      await Promise.allSettled(promises);
      
      // Combine results
      const combinedText = this.combineTextResults(gemmaResult, tesseractResult);
      
      const processingTime = performance.now() - startTime;
      
      // Create extraction result
      const result: ExtractionResult = {
        id: extractionId,
        documentId,
        timestamp: new Date(),
        processingTime,
        gemmaResult,
        tesseractResult,
        combinedText,
        status: errors.length === 0 ? 'success' : gemmaResult || tesseractResult ? 'partial' : 'failed',
        errors: errors.length > 0 ? errors : undefined,
      };
      
      // Save to database
      await db.saveExtraction({
        id: extractionId,
        documentId,
        extractionType: gemmaResult ? 'gemma' : 'tesseract',
        rawText: tesseractResult?.text || gemmaResult?.text || '',
        structuredData: result,
        confidence: this.calculateConfidence(gemmaResult, tesseractResult),
        processingTime,
        createdAt: result.timestamp,
        updatedAt: result.timestamp,
        corrections: [],
      });
      
      // Track analytics
      await db.trackAnalytics({
        type: 'performance',
        data: {
          event: 'document_processed',
          processingTime,
          engines: this.getEnginesUsed(options),
          success: result.status === 'success',
        },
      });
      
      return result;
      
    } catch (error) {
      console.error('Processing failed:', error);
      throw error;
    } finally {
      this.processingQueue.delete(extractionId);
    }
  }
  
  private async fileToImageData(file: File | Blob): Promise<ImageData> {
    const bitmap = await createImageBitmap(file);
    const canvas = new OffscreenCanvas(bitmap.width, bitmap.height);
    const ctx = canvas.getContext('2d') as OffscreenCanvasRenderingContext2D;
    ctx.drawImage(bitmap, 0, 0);
    return ctx.getImageData(0, 0, bitmap.width, bitmap.height);
  }
  
  private async preprocessImage(
    imageData: ImageData,
    options: ProcessingOptions['preprocessOptions']
  ): Promise<ImageData | null> {
    try {
      const result = await this.workerPool.queueTask({
        id: crypto.randomUUID(),
        type: 'preprocess',
        data: {
          action: options?.enhance ? 'enhance' : 'process',
          image: imageData,
          options,
        },
        priority: 5,
      });
      
      if (result.success && result.data) {
        // The worker sends back { success: true, action: 'process', result: {...} }
        // So we need to access result.data.result
        const preprocessResult = result.data.result || result.data;
        
        if (!preprocessResult) {
          console.error('No preprocessing result');
          return imageData;
        }
        
        const { data, width, height } = preprocessResult;
        
        // Validate dimensions
        if (!width || !height || width <= 0 || height <= 0) {
          console.error('Invalid image dimensions:', { width, height });
          return imageData; // Return original
        }
        
        // Ensure data is valid
        if (!data || !data.length) {
          console.error('Invalid image data');
          return imageData; // Return original
        }
        
        try {
          return new ImageData(new Uint8ClampedArray(data), width, height);
        } catch (error) {
          console.error('Failed to create ImageData:', error);
          return imageData; // Return original
        }
      }
      
      return imageData; // Return original if preprocessing failed
    } catch (error) {
      console.error('Preprocessing failed:', error);
      return null;
    }
  }
  
  private async runGemmaExtraction(
    imageData: ImageData,
    priority?: number
  ): Promise<ExtractionResult['gemmaResult']> {
    // Check if model is initialized
    const modelStatus = modelService.getStatus();
    if (!modelStatus.initialized) {
      // Try to initialize
      await modelService.initialize();
    }
    
    // Use worker for processing
    const result = await this.workerPool.queueTask({
      id: crypto.randomUUID(),
      type: 'gemma',
      data: {
        action: 'process',
        image: imageData,
        text: 'Extract all text, data, tables, and entities from this document.',
      },
      priority: priority || 10,
      timeout: 30000,
    });
    
    if (result.success) {
      // Handle both old and new response formats
      const gemmaResult = result.data?.result || result.data;
      
      if (gemmaResult) {
        return {
          text: gemmaResult.text || '',
          entities: gemmaResult.entities || [],
          tables: gemmaResult.tables || [],
          metadata: gemmaResult.metadata || {}
        };
      }
    }
    
    throw new Error(result.error || 'Gemma processing failed');
  }
  
  private async runTesseractExtraction(
    imageData: ImageData,
    options: ProcessingOptions
  ): Promise<ExtractionResult['tesseractResult']> {
    // Use worker for processing
    const result = await this.workerPool.queueTask({
      id: crypto.randomUUID(),
      type: 'tesseract',
      data: {
        action: 'process',
        image: imageData,
        language: options.language || 'eng',
        options: {
          psm: options.tesseractOptions?.psm || 3, // Fully automatic page segmentation
          whitelist: options.tesseractOptions?.whitelist,
        },
      },
      priority: options.priority || 8,
      timeout: 30000, // Increased timeout for real OCR
    });
    
    if (result.success) {
      // Handle both old and new response formats
      const ocrResult = result.data?.result || result.data;
      
      if (ocrResult && ocrResult.text) {
        return {
          text: ocrResult.text,
          confidence: ocrResult.confidence || 0,
          blocks: ocrResult.blocks || [],
          lines: ocrResult.lines || [],
          words: ocrResult.words || [],
          metadata: ocrResult.metadata || {
            language: options.language || 'eng',
            processingTime: result.processingTime,
            engine: 'tesseract.js'
          }
        };
      }
    }
    
    throw new Error(result.error || 'Tesseract processing failed');
  }
  
  private combineTextResults(
    gemmaResult?: ExtractionResult['gemmaResult'],
    tesseractResult?: ExtractionResult['tesseractResult']
  ): string {
    const texts: string[] = [];
    
    if (gemmaResult?.text) {
      texts.push(gemmaResult.text);
    }
    
    if (tesseractResult?.text && tesseractResult.text !== gemmaResult?.text) {
      texts.push(tesseractResult.text);
    }
    
    return texts.join('\n\n---\n\n');
  }
  
  private getEnginesUsed(options: ProcessingOptions): string[] {
    const engines: string[] = [];
    if (options.useGemma !== false) engines.push('gemma');
    if (options.useTesseract !== false) engines.push('tesseract');
    return engines;
  }
  
  private calculateConfidence(
    gemmaResult?: ExtractionResult['gemmaResult'],
    tesseractResult?: ExtractionResult['tesseractResult']
  ): number {
    const confidences: number[] = [];
    
    if (gemmaResult?.entities) {
      const avgEntityConfidence = gemmaResult.entities.reduce(
        (sum, e) => sum + e.confidence,
        0
      ) / (gemmaResult.entities.length || 1);
      confidences.push(avgEntityConfidence);
    }
    
    if (tesseractResult?.confidence) {
      confidences.push(tesseractResult.confidence / 100);
    }
    
    if (confidences.length === 0) return 0;
    
    return confidences.reduce((sum, c) => sum + c, 0) / confidences.length;
  }
  
  async getProcessingStatus() {
    const workerStatus = this.workerPool.getStatus();
    const processingDocuments = Array.from(this.processingQueue.entries()).map(
      ([id, options]) => ({
        id,
        options,
      })
    );
    
    return {
      workers: workerStatus,
      activeProcessing: processingDocuments.length,
      processingDocuments,
    };
  }
  
  async batchProcess(
    documents: Array<{ id: string; image: ImageData | File | Blob }>,
    options: ProcessingOptions = {}
  ): Promise<ExtractionResult[]> {
    const results: ExtractionResult[] = [];
    const batchSize = 3; // Process 3 documents in parallel
    
    for (let i = 0; i < documents.length; i += batchSize) {
      const batch = documents.slice(i, i + batchSize);
      const batchPromises = batch.map(doc =>
        this.processDocument(doc.id, doc.image, {
          ...options,
          priority: Math.max(1, (options.priority || 5) - Math.floor(i / batchSize)),
        })
      );
      
      const batchResults = await Promise.allSettled(batchPromises);
      
      for (const result of batchResults) {
        if (result.status === 'fulfilled') {
          results.push(result.value);
        } else {
          console.error('Batch processing error:', result.reason);
        }
      }
    }
    
    return results;
  }
  
  async warmupWorkers() {
    if (this.warmedUp) {
      console.log('Workers already warmed up');
      return;
    }
    
    console.log('Warming up AI workers...');
    this.warmedUp = true;
    
    const warmupTasks = [
      this.workerPool.queueTask({
        id: crypto.randomUUID(),
        type: 'gemma',
        data: {
          action: 'warmup',
        },
        priority: 1,
      }),
      
      this.workerPool.queueTask({
        id: crypto.randomUUID(),
        type: 'tesseract',
        data: {
          action: 'initialize',
          language: 'eng',
        },
        priority: 1,
      }),
    ];
    
    await Promise.allSettled(warmupTasks);
    console.log('Worker warmup complete');
  }
  
  terminate() {
    this.workerPool.terminate();
  }
}

// Export singleton
export const aiProcessingService = new AIProcessingService();

// React hook
export function useAIProcessing() {
  const [status, setStatus] = React.useState<any>(null);
  const [processing, setProcessing] = React.useState(false);
  
  React.useEffect(() => {
    // Warmup workers on mount
    aiProcessingService.warmupWorkers();
    
    // Update status periodically
    const interval = setInterval(async () => {
      const currentStatus = await aiProcessingService.getProcessingStatus();
      setStatus(currentStatus);
    }, 1000);
    
    return () => {
      clearInterval(interval);
    };
  }, []);
  
  const processDocument = React.useCallback(
    async (
      documentId: string,
      image: ImageData | File | Blob,
      options?: ProcessingOptions
    ) => {
      setProcessing(true);
      try {
        const result = await aiProcessingService.processDocument(
          documentId,
          image,
          options
        );
        return result;
      } finally {
        setProcessing(false);
      }
    },
    []
  );
  
  const batchProcess = React.useCallback(
    async (
      documents: Array<{ id: string; image: ImageData | File | Blob }>,
      options?: ProcessingOptions
    ) => {
      setProcessing(true);
      try {
        const results = await aiProcessingService.batchProcess(documents, options);
        return results;
      } finally {
        setProcessing(false);
      }
    },
    []
  );
  
  return {
    status,
    processing,
    processDocument,
    batchProcess,
  };
}